import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';

export type AuthMode = 'disabled' | 'oauth';

export interface ClickCiUser {
  id: string;
  qlikTenantId: string;
  qlikUserId: string;
  qlikSubject?: string;
  displayName: string;
  email?: string;
  pictureUrl?: string;
}

export interface GitHubConnection {
  userId: string;
  githubUserId: string;
  login: string;
  name?: string;
  email?: string;
  avatarUrl?: string;
  scopes: string[];
  connectedAt: string;
}

export interface AuthSessionState {
  mode: AuthMode;
  authenticated: boolean;
  user?: ClickCiUser;
  github?: GitHubConnection;
  expiresAt?: string;
  executors?: { qlik: string; git: string };
}

interface AuthContextValue extends AuthSessionState {
  loading: boolean;
  refresh: () => Promise<void>;
  logout: () => Promise<void>;
}

const demoSession: AuthSessionState = {
  mode: 'disabled',
  authenticated: true,
  user: {
    id: 'demo-user',
    qlikTenantId: 'demo',
    qlikUserId: 'demo-user',
    displayName: 'Bohua Li',
  },
  github: {
    userId: 'demo-user',
    githubUserId: 'demo',
    login: 'bohua',
    scopes: [],
    connectedAt: 'demo',
  },
  executors: { qlik: 'Preset service credential', git: 'Deployment SSH key' },
};

const AuthContext = createContext<AuthContextValue>({
  ...demoSession,
  loading: true,
  refresh: async () => undefined,
  logout: async () => undefined,
});

export const AuthProvider: React.FC = ({ children }) => {
  const [session, setSession] = useState<AuthSessionState>(demoSession);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    try {
      const response = await fetch('/api/auth/session', { credentials: 'include' });
      const payload = await response.json().catch(() => ({})) as Partial<AuthSessionState> & { authenticated?: boolean };
      // Old standalone development servers and existing mocked tests do not expose auth yet.
      if (typeof payload.authenticated !== 'boolean') {
        return;
      } else {
        setSession({
          mode: payload.mode === 'oauth' ? 'oauth' : 'disabled',
          authenticated: payload.authenticated,
          ...(payload.user ? { user: payload.user } : {}),
          ...(payload.github ? { github: payload.github } : {}),
          ...(payload.expiresAt ? { expiresAt: payload.expiresAt } : {}),
          ...(payload.executors ? { executors: payload.executors } : {}),
        });
        setLoading(false);
      }
    } catch {
      setSession({ mode: 'oauth', authenticated: false });
      setLoading(false);
    }
  }, []);

  useEffect(() => { void refresh(); }, [refresh]);

  const logout = useCallback(async () => {
    await fetch('/api/auth/logout', { method: 'POST', credentials: 'include' });
    setSession({ mode: 'oauth', authenticated: false });
  }, []);

  const value = useMemo(() => ({ ...session, loading, refresh, logout }), [session, loading, refresh, logout]);
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export function useAuth(): AuthContextValue {
  return useContext(AuthContext);
}

export function userInitials(name: string): string {
  return name.split(/\s+/).filter(Boolean).slice(0, 2).map(part => part[0]?.toUpperCase()).join('') || 'CI';
}
